import { getEnvironment } from "config/api";
import { useGetPaymentProviderQuery } from "store/queries/finix";

/**
 * Single source of truth for "which payment rail does this order use".
 *
 * Only a FLIPPED (Account.features.useFinix) account needs the authoritative
 * per-order lookup — to disambiguate a Stripe-era order from a new Finix order
 * (strict-freeze). A non-Finix account's orders are ALWAYS Stripe, so we skip
 * the `GET /finix/orders/:id/payment-provider` endpoint entirely for them. This
 * keeps the payment UI on its original, dependency-free path for every
 * Stripe-only account (100% of production today) and avoids adding a new
 * network/availability surface to those flows. The server-side processor guards
 * remain the financial backstop on both rails regardless.
 *
 * For a flipped account we always query the endpoint and trust its `provider`
 * (the account flag is only the pre-answer default while it resolves / for the
 * pre-order CreateOrder context). Consumers that must not render the wrong form
 * should also guard on `isLoading` / `isError`.
 */
export function useFinixPaymentProvider(orderInfo: any) {
  const accountFlag = !!orderInfo?.account?.features?.useFinix;
  const orderId = orderInfo?.id;

  // Non-Finix accounts never need the lookup — their orders are always Stripe.
  const shouldQuery = !!orderId && accountFlag;

  const {
    data: providerData,
    isLoading,
    isError,
  } = useGetPaymentProviderQuery(orderId, {
    skip: !shouldQuery,
  });

  const finixEnvironment: "sandbox" | "live" =
    getEnvironment() === "production" ? "live" : "sandbox";

  const isFinix = providerData
    ? providerData.provider === "FINIX"
    : accountFlag;

  return {
    isFinix,
    isLoading: shouldQuery ? isLoading : false,
    isError: shouldQuery ? isError : false,
    finixApplicationId: providerData?.finixApplicationId,
    finixMerchantId: providerData?.finixMerchantId,
    finixEnvironment,
  };
}
